import { useInfiniteQuery } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";
import { useMemo } from "react";

import {
  WhatsAppConversationMessagesResponse,
  WhatsAppMessage
} from "../../aiConversations.types";
import { AI_CONVERSATIONS_QUERY_KEY } from "../../constants/enums";
import { requestGetConversationMessages } from "./getConversationMessages.request";

interface Params {
  conversationId: string;
  take?: number;
}

const DEFAULT_TAKE = 30;

const useGetConversationMessages = (
  params: Params,
  options?: { enabled?: boolean }
) => {
  const { conversationId, take = DEFAULT_TAKE } = params;
  const enabled = (options?.enabled ?? true) && Boolean(conversationId);

  const query = useInfiniteQuery<
    WhatsAppConversationMessagesResponse,
    ApiServiceError<ApiError>
  >({
    queryKey: [AI_CONVERSATIONS_QUERY_KEY, "messages", conversationId],
    queryFn: async ({ pageParam = 1 }) =>
      await requestGetConversationMessages({
        conversationId,
        take,
        page: pageParam
      }),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.count / lastPage.take);
      const nextPage = lastPage.page + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    enabled,
    onError: toastServerError
  });

  const messages = useMemo<WhatsAppMessage[]>(() => {
    const pages = query.data?.pages ?? [];
    const flatDesc = pages.flatMap((p) => p.data);
    return flatDesc.slice().reverse();
  }, [query.data]);

  return {
    messages,
    count: query.data?.pages[0]?.count,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
};

export default useGetConversationMessages;
